export const angularQuestions = [
    // Core Concepts
    {
        id: 1,
        category: 'Angular Core',
        difficulty: 'Easy',
        question: 'What is the purpose of the decorator @Component in Angular?',
        options: [
            'It marks a class as an Angular component and provides configuration metadata',
            'It defines the routing for the application',
            'It creates a new service',
            'It handles HTTP requests'
        ],
        correct: 0,
        explanation: '@Component decorates a class to mark it as a component and provides metadata like selector, template, and style.'
    },
    {
        id: 2,
        category: 'Angular Core',
        difficulty: 'Easy',
        question: 'Which directive is used for two-way data binding in Angular?',
        options: [
            'ngModel',
            'ngFor',
            'ngIf',
            'ngBind'
        ],
        correct: 0,
        explanation: '[(ngModel)] syntax is used for two-way data binding, combining property binding and event binding.'
    },
    {
        id: 3,
        category: 'Angular Core',
        difficulty: 'Medium',
        question: 'What is the difference between Constructor and ngOnInit?',
        options: [
            'Constructor is a TS feature for class initialization; ngOnInit is an Angular lifecycle hook called after data-bound properties are initialized',
            'Constructor is for HTTP calls; ngOnInit is for variable assignment',
            'There is no difference, they are interchangeable',
            'ngOnInit runs before the constructor'
        ],
        correct: 0,
        explanation: 'The constructor is for dependency injection and class initialization. ngOnInit is where you should put logic that depends on Input properties.'
    },
    {
        id: 4,
        category: 'Angular Core',
        difficulty: 'Easy',
        question: 'How do you pass data from a parent component to a child component?',
        options: [
            'Using @Input decorator',
            'Using @Output decorator',
            'Using services only',
            'Using local storage'
        ],
        correct: 0,
        explanation: '@Input() allows data to flow into a component from its parent.'
    },
    {
        id: 5,
        category: 'Angular Core',
        difficulty: 'Easy',
        question: 'How do you emit an event from a child component to the parent?',
        options: [
            'Using @Output decorator with EventEmitter',
            'Using @Input decorator',
            'Using a callback function',
            'Using window.dispatchEvent'
        ],
        correct: 0,
        explanation: '@Output() combines with EventEmitter to allow a child to send custom events to a parent.'
    },

    // Dependency Injection & Services
    {
        id: 6,
        category: 'DI & Services',
        difficulty: 'Medium',
        question: 'What is a Singleton Service in Angular?',
        options: [
            'A service that has only one instance in the entire application scope (providedIn: "root")',
            'A service that can only be used once',
            'A service that has no dependencies',
            'A deprecated service type'
        ],
        correct: 0,
        explanation: 'Services provided in the root injector are singletons; the same instance is shared everywhere.'
    },
    {
        id: 7,
        category: 'DI & Services',
        difficulty: 'Medium',
        question: 'What is the purpose of the "providedIn" property in @Injectable?',
        options: [
            'It determines which injector will create the service',
            'It lists the service dependencies',
            'It defines the service name',
            'It restricts access to the service'
        ],
        correct: 0,
        explanation: 'providedIn specifies the injector where the service should be registered (e.g., "root").'
    },

    // RxJS & Observables
    {
        id: 8,
        category: 'RxJS',
        difficulty: 'Hard',
        question: 'What is the difference between an Observable and a Promise?',
        options: [
            'Observables handle multiple values over time and are cancellable; Promises handle a single value and are not cancellable',
            'Promises are faster than Observables',
            'Observables are native to JavaScript; Promises are external',
            'There is no difference'
        ],
        correct: 0,
        explanation: 'Observables are streams that can emit multiple values and can be cancelled (unsubscribed). Promises emit once (resolve/reject).'
    },
    {
        id: 9,
        category: 'RxJS',
        difficulty: 'Medium',
        question: 'Which RxJS operator is used to transform emitted values?',
        options: [
            'map',
            'filter',
            'tap',
            'switchMap'
        ],
        correct: 0,
        explanation: 'The map operator applies a projection function to each value emitted by the source Observable.'
    },
    {
        id: 10,
        category: 'RxJS',
        difficulty: 'Hard',
        question: 'What is a Subject in RxJS?',
        options: [
            'A special type of Observable that allows values to be multicasted to many Observers',
            'A type of subscription',
            'A way to define HTTP requests',
            'A function that returns a Promise'
        ],
        correct: 0,
        explanation: 'A Subject is like an EventEmitter: it determines when values are emitted and shares them among all subscribers (multicast).'
    },
    {
        id: 11,
        category: 'RxJS',
        difficulty: 'Hard',
        question: 'What does the "switchMap" operator do?',
        options: [
            'It maps to a new inner Observable and cancels the previous inner Observable',
            'It maps values one by one',
            'It merges all inner Observables',
            'It keeps all previous subscriptions active'
        ],
        correct: 0,
        explanation: 'switchMap is used for effects like autocomplete; it cancels pending requests if a new emission arrives from the source.'
    },

    // Templates & Directives
    {
        id: 12,
        category: 'Templates',
        difficulty: 'Easy',
        question: 'What syntax is used for property binding?',
        options: [
            '[property]="value"',
            '(property)="value"',
            '{property}="value"',
            '*property="value"'
        ],
        correct: 0,
        explanation: 'Square brackets [] are used for property binding (input).'
    },
    {
        id: 13,
        category: 'Templates',
        difficulty: 'Easy',
        question: 'Which structural directive conditionally includes a template?',
        options: [
            '*ngIf',
            '*ngFor',
            'ngStyle',
            'ngClass'
        ],
        correct: 0,
        explanation: '*ngIf adds or removes the element from the DOM based on the expression.'
    },
    {
        id: 14,
        category: 'Templates',
        difficulty: 'Medium',
        question: 'What is the "ng-container" element?',
        options: [
            'A logical container that does not render in the DOM, useful for grouping elements or applying structural directives',
            'A classic HTML div',
            'A component for layout',
            'A deprecated tag'
        ],
        correct: 0,
        explanation: 'ng-container allows you to apply directives like *ngIf without adding an extra wrapper element to the DOM.'
    },
    {
        id: 15,
        category: 'Templates',
        difficulty: 'Medium',
        question: 'What is the "trackBy" function used for in *ngFor?',
        options: [
            'To improve performance by uniquely identifying items, preventing unnecessary DOM re-renders',
            'To sort the list',
            'To filter the list',
            'To track user clicks'
        ],
        correct: 0,
        explanation: 'trackBy provides a unique identity for items, allowing Angular to reuse DOM elements when the list changes.'
    },

    // Routing
    {
        id: 16,
        category: 'Routing',
        difficulty: 'Easy',
        question: 'How do you define a route parameter in Angular?',
        options: [
            'path: "user/:id"',
            'path: "user/{id}"',
            'path: "user/?id"',
            'path: "user/<id>"'
        ],
        correct: 0,
        explanation: 'The colon syntax (:param) denotes a route parameter in the path definition.'
    },
    {
        id: 17,
        category: 'Routing',
        difficulty: 'Medium',
        question: 'What is a Route Guard?',
        options: [
            'An interface used to control access to routes (e.g., CanActivate)',
            'A CSS class for routes',
            'A method to stop HTTP requests',
            'A firewall setting'
        ],
        correct: 0,
        explanation: 'Guards return a boolean (or Observable/Promise of boolean) to decide if a navigation action should proceed.'
    },
    {
        id: 18,
        category: 'Routing',
        difficulty: 'Medium',
        question: 'What is Lazy Loading in Angular Routing?',
        options: [
            'Loading modules only when the user navigates to their route',
            'Loading images slowly',
            'Loading the whole app at startup independently',
            'Delaying HTTP requests'
        ],
        correct: 0,
        explanation: 'Lazy loading significantly improves startup time by splitting the app into feature blocks that load on demand.'
    },

    // Forms
    {
        id: 19,
        category: 'Forms',
        difficulty: 'Medium',
        question: 'What are the two approaches to handling forms in Angular?',
        options: [
            'Reactive Forms and Template-driven Forms',
            'HTML Forms and JS Forms',
            'Get Forms and Post Forms',
            'Static Forms and Dynamic Forms'
        ],
        correct: 0,
        explanation: 'Reactive Forms (model-driven) and Template-driven Forms are the two standard ways.'
    },
    {
        id: 20,
        category: 'Forms',
        difficulty: 'Medium',
        question: 'Which class is the base class for FormControl, FormGroup, and FormArray?',
        options: [
            'AbstractControl',
            'BaseControl',
            'ControlValueAccessor',
            'FormBase'
        ],
        correct: 0,
        explanation: 'AbstractControl provides the common behaviors (validity, value, status) for all form controls.'
    },

    // Change Detection & Lifecycle
    {
        id: 21,
        category: 'Lifecycle',
        difficulty: 'Hard',
        question: 'What is the "OnPush" change detection strategy?',
        options: [
            'Angular checks the component only when inputs change or events occur, skipping checks for other changes',
            'Angular checks the component on every mouse move',
            'Angular never checks the component',
            'It pushes data to the server'
        ],
        correct: 0,
        explanation: 'OnPush optimizes performance by disabling the default "check always" strategy. It relies on immutability or explicit detection.'
    },
    {
        id: 22,
        category: 'Lifecycle',
        difficulty: 'Medium',
        question: 'Which lifecycle hook is called just before the component is destroyed?',
        options: [
            'ngOnDestroy',
            'ngAfterViewInit',
            'ngOnChanges',
            'ngDoCheck'
        ],
        correct: 0,
        explanation: 'ngOnDestroy is the place to clean up subscriptions, timers, and event listeners.'
    },

    // Advanced & Modern Angular
    {
        id: 23,
        category: 'Advanced',
        difficulty: 'Hard',
        question: 'What are Standalone Components?',
        options: [
            'Components that do not need to be declared in an NgModule',
            'Components that run on a separate server',
            'Components that have no template',
            'Components that cannot use services'
        ],
        correct: 0,
        explanation: 'Introduced in Angular 14+, they simplify usage by removing the need for NgModules.'
    },
    {
        id: 24,
        category: 'Advanced',
        difficulty: 'Hard',
        question: 'What are Angular Signals (introduced in v16)?',
        options: [
            'A reactive primitive for managing state and granular updates',
            'A way to send WiFi signals',
            'A replacement for HTTP Client',
            'A styling library'
        ],
        correct: 0,
        explanation: 'Signals provide a new way to handle reactivity, offering fine-grained updates without Zone.js overhead.'
    },
    {
        id: 25,
        category: 'Modules',
        difficulty: 'Medium',
        question: 'What is the purpose of "declarations" in an NgModule?',
        options: [
            'To list the components, directives, and pipes that belong to this module',
            'To import other modules',
            'To provide services',
            'To export classes'
        ],
        correct: 0,
        explanation: 'Declarations make components available to the module\'s template context.'
    },
    {
        id: 26,
        category: 'HTTP',
        difficulty: 'Medium',
        question: 'What is an HttpInterceptor?',
        options: [
            'A way to intercept and modify HTTP requests or responses globally (e.g., adding Auth headers)',
            'A tool to block HTTP traffic',
            'A server-side component',
            'A caching mechanism'
        ],
        correct: 0,
        explanation: 'Interceptors are a powerful way to handle common concerns like authentication, error handling, and logging.'
    },
    {
        id: 27,
        category: 'Pipes',
        difficulty: 'Easy',
        question: 'What is a "pure" pipe?',
        options: [
            'A pipe that only re-evaluates when the input reference changes (stateless)',
            'A pipe that has no side effects',
            'A pipe that runs on every change detection cycle',
            'A pipe written in pure JS'
        ],
        correct: 0,
        explanation: 'Pure pipes are efficient because they do not run unless the input data reference updates.'
    },
    {
        id: 28,
        category: 'Directives',
        difficulty: 'Hard',
        question: 'How do you create a custom structural directive?',
        options: [
            'By injecting TemplateRef and ViewContainerRef in the directive constructor',
            'By using @Component',
            'By extending standard HTML',
            'Using ElementRef only'
        ],
        correct: 0,
        explanation: 'Structural directives need TemplateRef (what to render) and ViewContainerRef (where to render).'
    },
    {
        id: 29,
        category: 'Testing',
        difficulty: 'Medium',
        question: 'What is TestBed in Angular testing?',
        options: [
            'The primary API for configuring and initializing the environment for unit testing',
            'A separate browser for testing',
            'A library for E2E testing',
            'A database mock'
        ],
        correct: 0,
        explanation: 'TestBed creates a dynamic NgModule for testing components and services in isolation.'
    },
    {
        id: 30,
        category: 'Advanced',
        difficulty: 'Hard',
        question: 'What is ViewEncapsulation.ShadowDom?',
        options: [
            'It uses the browser\'s native Shadow DOM api to encapsulate styles',
            'It creates a shadow copy of the data',
            'It hides the element',
            'It disables styles'
        ],
        correct: 0,
        explanation: 'ShadowDom ensures true isolation; styles defined in the component do not leak out and global styles do not leak in.'
    },
    {
        id: 31,
        category: 'Core',
        difficulty: 'Easy',
        question: 'What is AOT compilation?',
        options: [
            'Ahead-of-Time compilation: compiles HTML/TS into efficient JS during the build phase',
            'All-of-Time compilation',
            'Angular-on-Target',
            'JIT compilation'
        ],
        correct: 0,
        explanation: 'AOT results in faster rendering and smaller bundles because the compiler runs before the browser sees the code.'
    },

    {
        id: 32,
        category: 'Security',
        difficulty: 'Medium',
        question: 'What does DomSanitizer do?',
        options: [
            'It sanitizes values to be safe to use in the DOM, preventing XSS attacks',
            'It cleans up unused DOM nodes',
            'It formats HTML code',
            'It encrypts data'
        ],
        correct: 0,
        explanation: 'Angular automatically sanitizes untrusted values, but DomSanitizer allows specific exceptions when you know a value is safe.'
    },

    // Signals & Reactivity (Deep Dive)
    {
        id: 33,
        category: 'Signals',
        difficulty: 'Medium',
        question: 'What is a "computed" signal?',
        options: [
            'A signal that derives its value from other signals and updates automatically when dependencies change',
            'A signal that calls a server',
            'A signal that is read-only',
            'A deprecated feature'
        ],
        correct: 0,
        explanation: 'Computed signals differ from standard signals as they are lazily evaluated and memoized based on their dependencies.'
    },
    {
        id: 34,
        category: 'Signals',
        difficulty: 'Hard',
        question: 'How does "effect()" differ from "computed()"?',
        options: [
            'Effect runs side effects (like logging or DOM updates) whenever dependencies change; Computed returns a value',
            'Effect returns a value; Computed runs side effects',
            'They are the same',
            'Effect is synchronous only'
        ],
        correct: 0,
        explanation: 'Use effect() for operations that leave the reactive context (like manually changing the DOM or logging), not for deriving data.'
    },

    // Performance Optimization
    {
        id: 35,
        category: 'Performance',
        difficulty: 'Hard',
        question: 'What is "Hydration" in Angular SSR?',
        options: [
            'The process of attaching event listeners to the static HTML generated by the server, making it interactive',
            'Downloading water assets',
            'Cleaning the cache',
            'Compiling the code'
        ],
        correct: 0,
        explanation: 'Hydration (re)uses the server-rendered DOM structures, attaching Angular behavior to them without destroying and repainting the page.'
    },
    {
        id: 36,
        category: 'Performance',
        difficulty: 'Medium',
        question: 'What is the purpose of "ng-content"?',
        options: [
            'Content projection: inserting content from a parent component into a child component\'s template',
            'To load external content',
            'To display text only',
            'To comment out code'
        ],
        correct: 0,
        explanation: 'ng-content acts as a placeholder where dynamic content passed from the parent will be rendered (similar to slots in other frameworks).'
    },
    {
        id: 37,
        category: 'Performance',
        difficulty: 'Hard',
        question: 'What is "Deferrable Views" (@defer)?',
        options: [
            'A template syntax to lazily load and render a block of content only when a trigger condition is met',
            'A way to defer HTTP requests',
            'A way to delay component destruction',
            'A type of promise'
        ],
        correct: 0,
        explanation: '@defer allows declarative lazy loading of content (e.g., "on viewport") without manual routing or complex logic.'
    },

    // State Management
    {
        id: 38,
        category: 'State Management',
        difficulty: 'Medium',
        question: 'What is the "Redux" pattern commonly used with NgRx?',
        options: [
            'Unidirectional data flow with a single source of truth (Store)',
            'Two-way data binding everywhere',
            'MVC pattern',
            'Service-oriented architecture'
        ],
        correct: 0,
        explanation: 'Redux provides predictable state management via Actions, Reducers, and Selectors.'
    },
    {
        id: 39,
        category: 'State Management',
        difficulty: 'Hard',
        question: 'What is an NgRx "Selector"?',
        options: [
            'A pure function used to slice and memoize a specific piece of state from the Store',
            'A CSS selector',
            'A function to update state',
            'A routing tool'
        ],
        correct: 0,
        explanation: 'Selectors are efficient because they only recompute when their specific arguments change, preventing unnecessary renders.'
    },

    // Testing
    {
        id: 40,
        category: 'Testing',
        difficulty: 'Hard',
        question: 'What are "Marble Diagrams" used for in testing?',
        options: [
            'To test asynchronous RxJS Observables visually by representing time and emissions',
            'To test CSS layouts',
            'To test database connections',
            'To optimize images'
        ],
        correct: 0,
        explanation: 'Marble testing allows you to assert precise timing and sequence of emissions in observable streams provided by TestScheduler.'
    },
    {
        id: 41,
        category: 'Testing',
        difficulty: 'Medium',
        question: 'What is the "fakeAsync" utility?',
        options: [
            'It runs asynchronous code in a synchronous way by controlling the timer (tick)',
            'It creates a fake component',
            'It creates a mock service',
            'It skips tests'
        ],
        correct: 0,
        explanation: 'fakeAsync allows you to write linear code for async logic by manually advancing time using tick() or flush().'
    },

    // Routing Advanced
    {
        id: 42,
        category: 'Routing',
        difficulty: 'Hard',
        question: 'What is the difference between "CanActivate" and "CanMatch" guards?',
        options: [
            'CanMatch controls whether the route definition creates a LoadChildren request; CanActivate runs after the route is matched',
            'CanMatch is deprecated',
            'CanActivate is for child routes only',
            'They are identical'
        ],
        correct: 0,
        explanation: 'CanMatch is more powerful for feature flipping because it can hide the existence of a route entirely, allowing fallback to wildcard routes.'
    },
    {
        id: 43,
        category: 'Routing',
        difficulty: 'Medium',
        question: 'How do you access Query Parameters in a component?',
        options: [
            'ActivatedRoute.queryParams',
            'Router.url',
            'Window.location',
            'Http.params'
        ],
        correct: 0,
        explanation: 'The ActivatedRoute service exposes an Observable stream of query parameters.'
    },

    // Directives Advanced
    {
        id: 44,
        category: 'Directives',
        difficulty: 'Hard',
        question: 'What is a "HostListener"?',
        options: [
            'A decorator that declares a DOM event to listen for and provides a handler method',
            'A server configuration',
            'A way to listen to HTTP requests',
            'A component lifecycle hook'
        ],
        correct: 0,
        explanation: '@HostListener allows a directive to subscribe to events on the host element (e.g., clicks, keypresses).'
    },
    {
        id: 45,
        category: 'Directives',
        difficulty: 'Hard',
        question: 'What is "HostBinding"?',
        options: [
            'A decorator used to set a property on the host element (e.g., class, style, attribute)',
            'A way to bind data to a service',
            'A way to host a website',
            'A database binding'
        ],
        correct: 0,
        explanation: '@HostBinding allows a directive/component to change the properties of the element it is attached to based on internal state.'
    },

    // DI Advanced
    {
        id: 46,
        category: 'DI & Services',
        difficulty: 'Hard',
        question: 'What is the "Self" resolution modifier (@Self)?',
        options: [
            'It tells the injector to look for the dependency only in the local injector (current component/directive)',
            'It looks in the parent component',
            'It looks in the root',
            'It creates a new instance'
        ],
        correct: 0,
        explanation: '@Self() ensures that the dependency must be provided on the same element; otherwise, it throws an error.'
    },
    {
        id: 47,
        category: 'DI & Services',
        difficulty: 'Hard',
        question: 'What is the "SkipSelf" resolution modifier?',
        options: [
            'It tells the injector to start the search in the parent injector, skipping the local one',
            'It skips the service',
            'It skips initialization',
            'It looks only in the root'
        ],
        correct: 0,
        explanation: '@SkipSelf() is useful when a component wants to isolate itself from valid providers created by itself or wants the parent\'s version.'
    },

    // Security & Best Practices
    {
        id: 48,
        category: 'Security',
        difficulty: 'Medium',
        question: 'What is CSP (Content Security Policy)?',
        options: [
            'An HTTP header that allows site operators to restrict the resources (JS, CSS, Images) that can load',
            'A password policy',
            'A firewall',
            'An encryption standard'
        ],
        correct: 0,
        explanation: 'CSP is a critical defense-in-depth layer against XSS/Injection attacks.'
    },
    {
        id: 49,
        category: 'Best Practices',
        difficulty: 'Medium',
        question: 'Why should you unsubscribe from Observables?',
        options: [
            'To prevent memory leaks and unexpected behavior from open streams',
            'To save bandwidth',
            'Because Angular requires it',
            'To make code faster'
        ],
        correct: 0,
        explanation: 'Failing to unsubscribe (especially in long-lived services or manual subscriptions) keeps the subscriber, context, and closures alive in memory.'
    },
    {
        id: 50,
        category: 'Best Practices',
        difficulty: 'Easy',
        question: 'What is the "AsyncPipe"?',
        options: [
            'A pipe that automatically subscribes to an Observable in the template and unsubscribes when the component is destroyed',
            'A pipe for faster rendering',
            'A pipe for sorting',
            'A pipe for HTTP'
        ],
        correct: 0,
        explanation: 'AsyncPipe (| async) is the preferred way to consume Observables in templates as it handles lifecycle management automatically.'
    },
    {
        id: 51,
        category: 'Architecture',
        difficulty: 'Hard',
        question: 'What is "Module Federation" in the context of Angular?',
        options: [
            'A method to share code between independent applications (Micro-Frontends) at runtime',
            'Combining all modules into one',
            'A federated login system',
            'A database sharding technique'
        ],
        correct: 0,
        explanation: 'Module Federation (Webpack 5+) allows loading remote Angular modules dynamically, key for building Micro-Frontend architectures.'
    }
];
